// @flow
import * as React from 'react';
import type { ChildrenArray, Element } from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Heading } from '../Heading.js';
import { Text } from '../Text.js';
import { CollapsibleInternal } from '../CollapsibleInternal.js';
import { productColor } from '../designSystem.js';
import { childrenArrayMatcher } from '../childrenArrayMatcher.js';

export function TSOStandardsSection(props: {|
  standardsName: string,
  children: ChildrenArray<
    | Element<typeof TSOMathematicalPracticeStandardsList>
    | Element<typeof TSOVirginiaMathematicalProcessGoalList>
    | Element<typeof TSOContentStandardsList>
    | Element<typeof TSOPriorConnectionsList>
    | Element<typeof TSOFutureConnectionsList>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, TSOStandardsSection);
  if (failedValidation) return null;

  return (
    <>
      <Heading>Standards</Heading>
      <div style={{ color: productColor.shuttleGrey }}>
        This subtopic addresses the following {props.standardsName} standards.
      </div>
      <div style={{ height: 16 }} />
      {props.children}
    </>
  );
}
TSOStandardsSection.spec = {
  standardsName: t.string,
  // TODO we can't currently express the actual requirements here which are.
  // - the precise order below is always adhered to
  // - TSOMathematicalPracticeStandardsList is optional
  // - One of (ContentStandards, PriorConnections, FutureConnections) must
  //   be present; any combination can be present; ordering must be followed;
  //
  // So what we are missing is the "at least one of these 3 must be present"
  children: t.exactOrderChildrenArray(
    t.optional(
      t.unionOfTypes(
        t.elementType(TSOMathematicalPracticeStandardsList),
        t.elementType(TSOVirginiaMathematicalProcessGoalList),
      ),
    ),
    t.optional(t.elementType(TSOContentStandardsList)),
    t.optional(t.elementType(TSOPriorConnectionsList)),
    t.optional(t.elementType(TSOFutureConnectionsList)),
  ),
};

export function TSOMathematicalPracticeStandardsList(props: {|
  children: ChildrenArray<Element<typeof TSOMathematicalPracticeStandard>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOMathematicalPracticeStandardsList,
  );
  if (failedValidation) return null;

  return (
    <InternalPanel title="Standards for Mathematical Practice">
      {interleaveSpacers(props.children)}
    </InternalPanel>
  );
}
TSOMathematicalPracticeStandardsList.spec = {
  children: t.childrenArray(t.elementType(TSOMathematicalPracticeStandard)),
};

export function TSOMathematicalPracticeStandard(props: {|
  code: string,
  children: ChildrenArray<
    | Element<typeof TSOMathematicalPracticeStandardDescription>
    | Element<typeof TSOMathematicalPracticeSuggestedApproach>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOMathematicalPracticeStandard,
  );
  if (failedValidation) return null;

  const [description, approach] = React.Children.toArray(props.children);

  return (
    <>
      <div style={{ fontWeight: 600 }}>
        ({props.code}) {description}
      </div>
      {approach !== undefined && (
        <>
          <div style={{ height: 8 }} />
          <div style={{ color: productColor.shuttleGrey }}>{approach}</div>
        </>
      )}
    </>
  );
}
TSOMathematicalPracticeStandard.spec = {
  code: t.string,
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(TSOMathematicalPracticeStandardDescription),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(TSOMathematicalPracticeSuggestedApproach),
    },
  ),
};

export function TSOMathematicalPracticeStandardDescription(props: {|
  children: Element<typeof Text>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOMathematicalPracticeStandardDescription,
  );
  if (failedValidation) return null;
  return React.cloneElement(props.children, { _display: 'inline' });
}
TSOMathematicalPracticeStandardDescription.spec = {
  children: t.elementType(Text),
};

export function TSOMathematicalPracticeSuggestedApproach(props: {|
  children: Element<typeof Text>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOMathematicalPracticeSuggestedApproach,
  );
  if (failedValidation) return null;
  return props.children;
}
TSOMathematicalPracticeSuggestedApproach.spec = {
  children: t.elementType(Text),
};

export function TSOVirginiaMathematicalProcessGoalList(props: {|
  children: ChildrenArray<Element<typeof TSOVirginiaMathematicalProcessGoal>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOVirginiaMathematicalProcessGoalList,
  );
  if (failedValidation) return null;

  return (
    <InternalPanel title="Mathematical Process Goals">
      {interleaveSpacers(props.children)}
    </InternalPanel>
  );
}
TSOVirginiaMathematicalProcessGoalList.spec = {
  children: t.childrenArray(t.elementType(TSOVirginiaMathematicalProcessGoal)),
};

export function TSOVirginiaMathematicalProcessGoal(props: {|
  code: string,
  children: ChildrenArray<
    | Element<typeof TSOVirginiaMathematicalProcessGoalDescription>
    | Element<typeof TSOVirginiaMathematicalProcessGoalSuggestedApproach>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOVirginiaMathematicalProcessGoal,
  );
  if (failedValidation) return null;

  const [description, approach] = React.Children.toArray(props.children);

  return (
    <>
      <div style={{ fontWeight: 600 }}>
        ({props.code}) {description}
      </div>
      {approach !== undefined && (
        <>
          <div style={{ height: 8 }} />
          <div style={{ color: productColor.shuttleGrey }}>{approach}</div>
        </>
      )}
    </>
  );
}
TSOVirginiaMathematicalProcessGoal.spec = {
  code: t.string,
  children: childrenArrayMatcher(
    {
      min: 1,
      max: 1,
      elementType: t.elementType(TSOVirginiaMathematicalProcessGoalDescription),
    },
    {
      min: 0,
      max: 1,
      elementType: t.elementType(
        TSOVirginiaMathematicalProcessGoalSuggestedApproach,
      ),
    },
  ),
};

export function TSOVirginiaMathematicalProcessGoalDescription(props: {|
  children: Element<typeof Text>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOVirginiaMathematicalProcessGoalDescription,
  );
  if (failedValidation) return null;
  return React.cloneElement(props.children, { _display: 'inline' });
}
TSOVirginiaMathematicalProcessGoalDescription.spec = {
  children: t.elementType(Text),
};

export function TSOVirginiaMathematicalProcessGoalSuggestedApproach(props: {|
  children: Element<typeof Text>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOVirginiaMathematicalProcessGoalSuggestedApproach,
  );
  if (failedValidation) return null;
  return props.children;
}
TSOVirginiaMathematicalProcessGoalSuggestedApproach.spec = {
  children: t.elementType(Text),
};

export function TSOContentStandardsList(props: {|
  children: ChildrenArray<Element<typeof TSOContentStandard>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOContentStandardsList,
  );
  if (failedValidation) return null;
  return (
    <InternalPanel title="Content standards">
      {interleaveSpacers(props.children)}
    </InternalPanel>
  );
}
TSOContentStandardsList.spec = {
  children: t.childrenArray(t.elementType(TSOContentStandard)),
};

export function TSOPriorConnectionsList(props: {|
  children: ChildrenArray<Element<typeof TSOContentStandard>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOPriorConnectionsList,
  );
  if (failedValidation) return null;
  return (
    <InternalPanel title="Prior connections">
      {interleaveSpacers(props.children)}
    </InternalPanel>
  );
}
TSOPriorConnectionsList.spec = {
  children: t.childrenArray(t.elementType(TSOContentStandard)),
};

export function TSOFutureConnectionsList(props: {|
  children: ChildrenArray<Element<typeof TSOContentStandard>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOFutureConnectionsList,
  );
  if (failedValidation) return null;

  return (
    <InternalPanel title="Future connections">
      {interleaveSpacers(props.children)}
    </InternalPanel>
  );
}
TSOFutureConnectionsList.spec = {
  children: t.childrenArray(t.elementType(TSOContentStandard)),
};

export function TSOContentStandard(props: {|
  code: string,
  children: Element<typeof TSOContentStandardDescription>,
|}) {
  const { failedValidation } = usePropsValidation(props, TSOContentStandard);
  if (failedValidation) return null;
  return (
    <>
      <div style={{ fontWeight: 600, color: productColor.cloudburst }}>
        {props.code}
      </div>
      <div style={{ height: 8 }} />
      <div style={{ color: productColor.shuttleGrey }}>{props.children}</div>
    </>
  );
}
TSOContentStandard.spec = {
  code: t.string,
  children: t.elementType(TSOContentStandardDescription),
};

export function TSOContentStandardDescription(props: {|
  children: Element<typeof Text>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    TSOContentStandardDescription,
  );
  if (failedValidation) return null;
  return props.children;
}
TSOContentStandardDescription.spec = {
  children: t.elementType(Text),
};

// Supporting components
function InternalPanel({
  title,
  children,
}: {|
  title: string,
  children: React$Node,
|}) {
  return (
    <div
      style={{
        backgroundColor: productColor.seashell,
        padding: 20,
        paddingTop: 1,
        paddingBottom: 1,
        borderRadius: 8,
        marginBottom: 20,
      }}
    >
      <CollapsibleInternal
        hideHorizontalRule
        openByDefault
        title={title}
        customChevron={CollapseChevron}
      >
        {children}
      </CollapsibleInternal>
    </div>
  );
}

function CollapseChevron({ isOpen }) {
  return (
    <svg
      width="12"
      height="7.5"
      viewBox="0 0 8 5"
      style={{ transform: `rotate(${isOpen ? 180 : 0}deg)` }}
    >
      <path
        d="M3.60957 4.51196L0.649878 0.812348C0.387973 0.484966 0.621059 0 1.04031 0H6.95969C7.37894 0 7.61203 0.484966 7.35012 0.812348L4.39043 4.51196C4.19027 4.76216 3.80973 4.76216 3.60957 4.51196Z"
        fill={productColor.cloudburst}
      />
    </svg>
  );
}

function interleaveSpacers(childrenArray, size = 16) {
  // NOTE we lose keys here, but we don't use them in authored content
  const children = React.Children.toArray(childrenArray);
  let result = [];
  for (let i = 0; i < children.length; i++) {
    result.push(React.cloneElement(children[i], { key: `c-${i}` }));
    if (i !== children.length - 1) {
      result.push(<div key={`s-${i}`} style={{ height: size }} />);
    }
  }
  return result;
}
